// Shared geocoding for the facility, operator and hospital forms.

const GEOCODE_URL = 'https://maps.googleapis.com/maps/api/geocode/json'

export const GEOCODE_FAILED_NOTICE =
  'Saved, but the address could not be geocoded — map coordinates were left unchanged.'

// Resolve an address to coordinates. Returns null when Google cannot geocode the
// address (ZERO_RESULTS) or the API itself is unavailable (OVER_QUERY_LIMIT,
// REQUEST_DENIED, network error). Callers must treat null as "save without
// coordinates" — a failed lookup is never a reason to block a create or an edit.
export async function geocodeAddress(data) {
  const address = `${data.address}, ${data.city}, ${data.state} ${data.zip}`
  try {
    const res = await fetch(
      `${GEOCODE_URL}?address=${encodeURIComponent(address)}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`
    ).then(res => res.json())

    if (res.status !== 'OK' || !res.results?.[0]) return null

    const result = res.results[0]
    return {
      lat: result.geometry.location.lat,
      long: result.geometry.location.lng,
      formatted_address: result.formatted_address,
    }
  } catch {
    return null
  }
}

// Keys to merge into a save payload.
//
// `coordinateOrder` is deliberately explicit because the two collections differ:
//
//   'long-lat'  Correct GeoJSON. Used for Organizations (hospitals). Nothing reads
//               Organization.location geospatially and there is no legacy data, so
//               this is safe to get right with no migration.
//
//   'lat-long'  Wrong per the spec, but it is how every Facilities document is
//               already stored and how routes/api/facilities.js queries them via
//               $nearSphere. Both sides are swapped, so results are self-consistent.
//               Flipping the write alone would drop every existing facility out of
//               proximity search — correcting it needs a data migration, which the
//               team has chosen not to run.
//
// Known limitation of 'lat-long': the 2dsphere index rejects a point whose second
// element falls outside ±90, so a facility west of ~90°W longitude (TX, CO, AZ,
// CA...) cannot be saved. That is pre-existing and only a migration can resolve it.
//
// Returns {} when geocoding failed, so the keys are omitted entirely: an edit then
// preserves whatever is already stored and a create simply has no point. Never emit
// coordinates: [null, null] — the 2dsphere index rejects that too.
export function locationPayload(cords, { includeCordsString = false, coordinateOrder } = {}) {
  if (!cords || typeof cords.lat !== 'number' || typeof cords.long !== 'number') return {}

  if (coordinateOrder !== 'long-lat' && coordinateOrder !== 'lat-long') {
    throw new Error("locationPayload requires an explicit coordinateOrder of 'long-lat' or 'lat-long'")
  }

  const coordinates = coordinateOrder === 'long-lat'
    ? [cords.long, cords.lat]
    : [cords.lat, cords.long]

  const payload = {
    location: { type: 'Point', coordinates },
    formatted_address: cords.formatted_address,
  }

  // Organization.cords is a String column; Facilities has no such field.
  if (includeCordsString) payload.cords = JSON.stringify({ lat: cords.lat, long: cords.long })

  return payload
}
